import { Page, Text, View, Document, StyleSheet, Image, Svg, Polyline } from '@react-pdf/renderer';
import logo from "@/assets/ppm-logo.png";
import { format } from 'date-fns';
import { dateformat } from '@/utils/GlobalConstants';
import { StatusReport } from '@/Pages/StatusReport';

// Helper function to convert HSL CSS variable to hex color for react-pdf
function hslToHex(h: number, s: number, l: number): string {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

// Get theme colors from CSS variables
function getThemeColors() {
    const root = getComputedStyle(document.documentElement);
    
    const parseHSL = (varName: string): string => {
        const value = root.getPropertyValue(varName).trim();
        const parts = value.split(/[\s,]+/).map(v => parseFloat(v));
        if (parts.length >= 3) {
            return hslToHex(parts[0], parts[1], parts[2]);
        }
        return '#000000';
    };

    return {
        header: parseHSL('--table-header'),
        headerForeground: parseHSL('--table-header-foreground'),
        background: parseHSL('--background'),
        foreground: parseHSL('--foreground'),
        border: parseHSL('--border'),
        cardBackground: parseHSL('--card'),
        chartRed: parseHSL('--chart-red'),
        chartAmber: parseHSL('--chart-amber'),
        chartGreen: parseHSL('--chart-green'),
        tableAlternate: parseHSL('--table-alternate-row'),
    };
}

// Create styles function that takes theme colors
const createStyles = (colors: ReturnType<typeof getThemeColors>) => StyleSheet.create({
    header: {
        flexDirection: 'row',
        backgroundColor: colors.header,
        color: colors.headerForeground,
        padding: 5,
        fontWeight: 'heavy',
    },
    fieldHeader: {
        backgroundColor: colors.header,
        color: colors.headerForeground,
        fontWeight: 'heavy',
        border: 'none',
    },
    text: {
        fontSize: 10,
        padding: 5,
        textAlign: 'justify',
        fontFamily: 'Times-Roman',
        color: colors.foreground,
    },
    page: {
      flexDirection: 'column',
      backgroundColor: colors.background
    },
    body: {
        flexDirection: 'row',
    },
    section: {
        flexDirection: 'column',
        width: '50%',
        paddingHorizontal: 10
    }
});

function getStatusColor(status: string | number, colors: ReturnType<typeof getThemeColors>) {
    if (typeof status === 'string') {
        if (status === 'Low' || status === 'green') {
            return colors.chartGreen;
        } else if (status === 'Medium' || status === 'amber') {
            return colors.chartAmber;
        } else if (status === 'High' || status === 'red') {
            return colors.chartRed;
        } else {
            return colors.chartGreen;
        }
    }
    if (typeof status === 'number') {
    switch (status) {
        case 1:
            return colors.chartRed;
        case 2:
            return colors.chartAmber;
        case 4:
            return colors.chartGreen;
        default:
            return colors.tableAlternate;
    }
}
}

interface StatusReportProps {
    reportData: StatusReport
    projectType: number
}

function StatusReportPDF({ reportData, projectType }: StatusReportProps) {
    const colors = getThemeColors();
    const styles = createStyles(colors);

    function getTitle(workstreamName: string, projectType: number) {
        if (projectType === 1) {
            return "Project Status Report";
        } else if (projectType === 2) {
            return workstreamName + " Status Report";
        }
    }

    return (
        <Document title={"Status Report"} author="Explic8">
            <Page size="A4" orientation='landscape' style={styles.page}>
                <View style={styles.header}>
                    <Text>{getTitle(reportData.workstreamName, projectType)}</Text>
                </View>
                <Image src={logo} style={{width: "120px", marginVertical: "5px", marginHorizontal:"10px"}}/> 
                <View style={styles.body}>
                    <View style={styles.section}>
                        <View style={{flexDirection: 'row', marginBottom: '10px', borderColor: colors.border, borderWidth: 1, borderRadius: 5, width: '80%'}}><Text style={[styles.fieldHeader, styles.text,{borderTopLeftRadius: 4, borderBottomLeftRadius: 4}]}>Report Date: </Text><Text style={styles.text}>{format(new Date(reportData.date), dateformat)}</Text></View>
                        <View style={{flexDirection: 'column', marginBottom: '10px', borderColor: colors.border, borderWidth: 1, borderRadius: 5, width: '80%'}}>
                            <Text style={[styles.fieldHeader,styles.text,{borderTopRightRadius: 4, borderTopLeftRadius: 4},{paddingBottom:2}]}>{projectType === 1 ? "Project " : "Workstream "}Health Check</Text>
                            <View style={{flexDirection: 'row'}}>
                                <View style={{flexDirection: 'column'}}><Text style={[styles.fieldHeader, styles.text]}>Est. Close Date</Text><Text style={styles.text}>{format(new Date(reportData.estimatedCloseDate),dateformat)}</Text></View>
                                <View style={{flexDirection: 'column'}}><Text style={[styles.fieldHeader, styles.text]}>Financial</Text><View style={{ backgroundColor: getStatusColor(reportData.health.financial, colors), borderRadius: '50%', width: '15px', height: '15px', marginVertical: '5px', marginHorizontal: 'auto' }}/></View>
                                <View style={{flexDirection: 'column'}}><Text style={[styles.fieldHeader, styles.text]}>Schedule</Text><View style={{ backgroundColor: getStatusColor(reportData.health.schedule, colors), borderRadius: '50%', width: '15px', height: '15px', marginVertical: '5px', marginHorizontal: 'auto' }}/></View>
                                <View style={{flexDirection: 'column'}}><Text style={[styles.fieldHeader, styles.text]}>Resources</Text><View style={{ backgroundColor: getStatusColor(reportData.health.resources, colors), borderRadius: '50%', width: '15px', height: '15px', marginVertical: '5px', marginHorizontal: 'auto' }}/></View>
                                <View style={{flexDirection: 'column'}}><Text style={[styles.fieldHeader, styles.text]}>Scope</Text><View style={{ backgroundColor: getStatusColor(reportData.health.scope, colors), borderRadius: '50%', width: '15px', height: '15px', marginVertical: '5px', marginHorizontal: 'auto' }}/></View>
                                <View style={{flexDirection: 'column'}}><Text style={[styles.fieldHeader, styles.text]}>Other Issue/Risk</Text><View style={{ backgroundColor: getStatusColor(reportData.health.other, colors), borderRadius: '50%', width: '15px', height: '15px', marginVertical: '5px', marginHorizontal: 'auto' }}/></View>
                            </View>
                        </View>
                        <View style={{flexDirection: 'column', borderColor: colors.border, borderWidth: 1, borderRadius: 5, width: '100%'}}>
                            <View style={{flexDirection: 'row'}}>
                                <Text style={[styles.fieldHeader,styles.text,{borderTopLeftRadius: 4, width:'4%'}]}>#</Text>
                                <Text style={[styles.fieldHeader,styles.text,{width:'50%'}]}>Key Milestones</Text>
                                <Text style={[styles.fieldHeader,styles.text,{width:'18%'}]}>Planned Finish</Text>
                                <Text style={[styles.fieldHeader,styles.text,{width:'10%'}]}>Status</Text>
                                <Text style={[styles.fieldHeader,styles.text,{borderTopRightRadius: 4, width:'18%'}]}>Actual Finish</Text>
                            </View>
                            {reportData.workPackages.length > 0 ? reportData.workPackages.map((workPackage) => {
                                return (
                                    <View style={{flexDirection: 'column', width: '100%'}}>
                                        <View style={{flexDirection:'row', backgroundColor: colors.tableAlternate}}><View style={{width:'10%'}} /><View style={{width:'90%'}} ><Text style={styles.text}>{workPackage.name}</Text></View></View>
                                        {/* Map over keyMilestones*/}
                                        {workPackage.tasks.map((keyMilestone) => {
                                            return (
                                                <View style={{flexDirection: 'row'}}>
                                                    <View style={{flexDirection: 'column', width: '4%', paddingTop: 5, paddingLeft: 5}}>
                                                        {keyMilestone.completed ? (
                                                            <Svg style={{ height: 15, width: 15 }}>
                                                                <Polyline points="0,6 3,9 9,0" stroke={getStatusColor('green', colors)} strokeWidth={2} />
                                                            </Svg>
                                                        ) : (
                                                            <Text style={styles.text}></Text>
                                                        )}
                                                    </View>
                                                    <View style={{flexDirection: 'column', width: '50%'}}>
                                                        <Text style={styles.text}>{keyMilestone.name}</Text>
                                                    </View>
                                                    <View style={{flexDirection: 'column', width: '18%'}}>
                                                        <Text style={styles.text}>{format(new Date(keyMilestone.finishDate), dateformat)}</Text>
                                                    </View>
                                                    <View style={{flexDirection: 'column', width: '10%'}}>
                                                        <View style={{ backgroundColor: getStatusColor(keyMilestone.status, colors), borderRadius: '50%', width: '15px', height: '15px', marginVertical: '2px', marginHorizontal: 'auto' }} />
                                                    </View>
                                                    <View style={{flexDirection: 'column', width: '18%'}}>
                                                        <Text style={styles.text}>{keyMilestone.completed ? format(new Date(keyMilestone.completedDate), dateformat) : "-"}</Text>
                                                    </View>
                                                </View>
                                            )
                                        })}
                                    </View>
                                )
                            }) : (
                                <View style={{flexDirection: 'row'}}>
                                    <View style={{flexDirection: 'column', width: '100%'}}>
                                        <Text style={styles.text}>No Work Packages</Text>
                                    </View>
                                </View>
                            )}
                        </View>
                    </View>
                    <View style={styles.section}>{/* Right side of the page */}
                        <View style={{flexDirection: 'column', marginBottom: '10px', borderColor: colors.border, borderWidth: 1, borderRadius: 5, width: '100%'}}>
                            <Text style={[styles.fieldHeader,styles.text,{borderTopRightRadius: 4, borderTopLeftRadius: 4, paddingBottom:2}]}>Support Requests</Text>
                            <View style={{flexDirection: 'row'}}>
                                <View style={{flexDirection: 'column', width: '80%'}}>
                                    <Text style={[styles.fieldHeader, styles.text]}>Request</Text>
                                </View>
                                <View style={{flexDirection: 'column', width: '20%'}}>
                                    <Text style={[styles.fieldHeader, styles.text]}>Date Required</Text>
                                </View>
                            </View>
                            {reportData.supportRequests.length > 0 ? reportData.supportRequests.map((request) => {
                                return (
                                    <View style={{flexDirection: 'row'}}>
                                        <View style={{flexDirection: 'column', width: '80%'}}>
                                            <Text style={styles.text}>{request.description}</Text>
                                        </View>
                                        <View style={{flexDirection: 'column', width: '20%'}}>
                                            <Text style={styles.text}>{format(new Date(request.date), dateformat)}</Text>
                                        </View>
                                    </View>
                                )
                            }) : (
                                <View style={{flexDirection: 'row'}}>
                                    <View style={{flexDirection: 'column', width: '80%'}}>
                                        <Text style={styles.text}>No Support Requests</Text>
                                    </View>
                                    <View style={{flexDirection: 'column', width: '20%'}}>
                                        <Text style={styles.text}>-</Text>
                                    </View>
                                </View>
                            )}
                        </View>
                        <View style={{flexDirection: 'column', marginBottom: '10px', borderColor: colors.border, borderWidth: 1, borderRadius: 5, width: '100%'}}>
                            <Text style={[styles.fieldHeader,styles.text,{borderTopRightRadius: 4, borderTopLeftRadius: 4, paddingBottom:2}]}>Risk Issues</Text>
                            <View style={{flexDirection: 'row'}}>
                                <View style={{flexDirection: 'column', width: '40%'}}>
                                    <Text style={[styles.fieldHeader, styles.text]}>Risk</Text>
                                </View>
                                <View style={{flexDirection: 'column', width: '40%'}}>
                                    <Text style={[styles.fieldHeader, styles.text]}>Mitigation</Text>
                                </View>
                                <View style={{flexDirection: 'column', width: '20%'}}>
                                    <Text style={[styles.fieldHeader, styles.text]}>Rating</Text>
                                </View>
                            </View>
                            {reportData.riskIssues.length > 0 ? reportData.riskIssues.map((risk) => {
                                return (
                                    <View style={{flexDirection: 'row'}}>
                                        <View style={{flexDirection: 'column', width: '40%'}}>
                                            <Text style={styles.text}>{risk.description}</Text>
                                        </View>
                                        <View style={{flexDirection: 'column', width: '40%'}}>
                                            <Text style={styles.text}>{risk.mitigation}</Text>
                                        </View>
                                        <View style={{flexDirection: 'column', width: '20%'}}>
                                            <View style={{ backgroundColor: getStatusColor(risk.rating, colors), borderRadius: '50%', width: '15px', height: '15px', marginVertical: '2px', marginHorizontal: 'auto' }} />
                                        </View>
                                    </View>
                                )
                            }) : (
                                <View style={{flexDirection: 'row'}}>
                                    <View style={{flexDirection: 'column', width: '40%'}}>
                                        <Text style={styles.text}>No Risk Issues</Text>
                                    </View>
                                    <View style={{flexDirection: 'column', width: '40%'}}>
                                        <Text style={styles.text}>-</Text>
                                    </View>
                                    <View style={{flexDirection: 'column', width: '20%'}}>
                                        <Text style={styles.text}>-</Text>
                                    </View>
                                </View>
                            )}
                        </View>
                        <View style={{flexDirection: 'column', marginBottom: '10px', borderColor: colors.border, borderWidth: 1, borderRadius: 5, width: '100%'}}>
                            <Text style={[styles.fieldHeader,styles.text,{borderTopRightRadius: 4, borderTopLeftRadius: 4}]}>Activities</Text>
                            <Text style={styles.text}>{reportData.activitiesSince ? reportData.activitiesSince : "No Activities Since"}</Text>
                        </View>
                        <View style={{flexDirection: 'column', marginBottom: '10px', borderColor: colors.border, borderWidth: 1, borderRadius: 5, width: '100%'}}>
                            <Text style={[styles.fieldHeader,styles.text,{borderTopRightRadius: 4, borderTopLeftRadius: 4}]}>Next Steps</Text>
                            <Text style={styles.text}>{reportData.nextSteps ? reportData.nextSteps : "No Next Steps"}</Text>
                        </View>
                        <View style={{flexDirection: 'row', borderColor: colors.border, borderWidth: 1, borderRadius: 5, width: '100%'}}>
                            <Text style={styles.text}>Status Legend: </Text>
                            <View style={{flexDirection: 'row', width: '80%', margin:'auto'}}>
                                <View style={{flexDirection: 'row', marginRight:5}}><View style={{ backgroundColor: getStatusColor(4, colors), borderRadius: '50%', width: '15px', height: '15px', marginVertical: '2px', marginHorizontal: 'auto' }} /><Text style={styles.text}>on Track</Text></View>
                                <View style={{flexDirection: 'row', marginRight:5}}><View style={{ backgroundColor: getStatusColor(2, colors), borderRadius: '50%', width: '15px', height: '15px', marginVertical: '2px', marginHorizontal: 'auto' }} /><Text style={styles.text}>at Risk</Text></View>
                                <View style={{flexDirection: 'row', marginRight:5}}><View style={{ backgroundColor: getStatusColor(1, colors), borderRadius: '50%', width: '15px', height: '15px', marginVertical: '2px', marginHorizontal: 'auto' }} /><Text style={styles.text}>Not on Track</Text></View>
                                <View style={{flexDirection: 'row'}}><View style={{paddingTop:5}}><Svg style={{height: 15, width: 15}}><Polyline points='0,6 3,9 9,0' stroke={getStatusColor('green', colors)} strokeWidth={2} /></Svg></View><Text style={styles.text}>Completed</Text></View>
                            </View>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    )
}

export default StatusReportPDF